import { Box, Button, IconButton, Stack, Tooltip, Typography } from '@mui/material';
import { ContentCopyOutlined, OpenInNewOutlined } from '@mui/icons-material';
import { FunctionComponent, PropsWithChildren, useContext } from 'react';
import { UserNotificationsContext } from '../../../context/user-notifications/user-notifications';

const SUGGESTED_DESCRIPTION = 'Carbon reporting (read only)';
const SUGGESTED_PERMISSION_SET = 'D365 READ';

/*
 * The one step of connecting Business Central that happens inside Business Central: giving our
 * application a Microsoft Entra Applications record there. Microsoft access alone is not enough,
 * because Business Central keeps its own list of what each application may read.
 *
 * entraApplicationsUrl (built by the backend) opens that page directly in the right environment,
 * and in the right tenant once a sign-in has linked one, so nobody searches for it or does it in
 * another environment. Without it the steps say how to find the page.
 *
 * Shown wherever someone may be stuck on this step: the connect panel, the details page when
 * Business Central refuses the application, and the public page an administrator lands on after
 * granting access. Kept in step with the emailed instructions (businesscentral-instructions.helper.js
 * in the backend).
 */

/** The same steps as plain text, to send to whoever administers Business Central. */
export const businessCentralAppStepsText = (clientId: string, entraApplicationsUrl?: string | null): string => [
  'Allow the application in Business Central (a Business Central administrator does this once):',
  entraApplicationsUrl
    ? `1. Open Microsoft Entra Applications in Business Central (${entraApplicationsUrl}), then choose New.`
    : '1. In Business Central, use the search (the magnifying glass at the top, or Alt+Q) to find Microsoft Entra Applications, then choose New.',
  `2. In Client ID, enter ${clientId}`,
  `3. In Description, enter a name your team will recognise, for example "${SUGGESTED_DESCRIPTION}".`,
  '4. Set State to Enabled.',
  `5. Under User Permission Sets, add ${SUGGESTED_PERMISSION_SET}, which only reads. Leave Company blank to cover every company, or add one line per company to report.`,
  '6. Skip Grant Consent on that page: access is granted in Microsoft instead.',
].join('\n');

/** A Business Central field or action name, set apart from the sentence around it. */
const Term: FunctionComponent<PropsWithChildren> = ({ children }) => (
  <Typography component='span' variant='body2' sx={{ color: 'text.primary' }}>{children}</Typography>
);

export const BusinessCentralAppSteps: FunctionComponent<{ clientId: string, entraApplicationsUrl?: string | null }> = ({ clientId, entraApplicationsUrl }) => {
  const userNotificationsContext = useContext(UserNotificationsContext);

  // Optional call: the public page can render outside the notifications provider in tests.
  const copyClientId = () => {
    navigator.clipboard.writeText(clientId)
      .then(() => userNotificationsContext.openSnackbar?.({ open: true, message: 'Client id copied to your clipboard', backgroundColor: 'text.primary' }))
      .catch(() => userNotificationsContext.openSnackbar?.({ open: true, message: 'Could not copy; select the text by hand', backgroundColor: 'text.primary' }));
  };

  return (
    <Stack spacing={1.5}>
      {entraApplicationsUrl && (
        <Box>
          <Button variant='outlined' component='a' href={entraApplicationsUrl} target='_blank' rel='noopener noreferrer' startIcon={<OpenInNewOutlined fontSize='small' />}>
            <Typography sx={{ pl: 0.5 }}>Open in Business Central</Typography>
          </Button>
        </Box>
      )}
      <Typography component='ol' variant='body2' sx={{ color: 'text.secondary', pl: 2.5, m: 0, maxWidth: '680px', '& li': { mb: 0.75 } }}>
        {entraApplicationsUrl ? (
          <li>
            Choose <Term>Open in Business Central</Term> to go straight to <Term>Microsoft Entra Applications</Term>,
            then choose <Term>New</Term>.
          </li>
        ) : (
          <li>
            In Business Central, use the search (the magnifying glass at the top, or Alt+Q) to find{' '}
            <Term>Microsoft Entra Applications</Term>, then choose <Term>New</Term>.
          </li>
        )}
        <li>
          <Stack direction='row' alignItems='center' gap={1} component='span' sx={{ display: 'inline-flex', flexWrap: 'wrap' }}>
            <span>In <Term>Client ID</Term>, enter</span>
            <Typography component='span' variant='body2' sx={{ fontFamily: 'monospace', color: 'text.primary', wordBreak: 'break-all' }}>{clientId}</Typography>
            <Tooltip title='Copy'>
              <IconButton size='small' aria-label='Copy client id' onClick={copyClientId}>
                <ContentCopyOutlined fontSize='small' />
              </IconButton>
            </Tooltip>
          </Stack>
        </li>
        <li>
          In <Term>Description</Term>, enter a name your team will recognise, for example &ldquo;{SUGGESTED_DESCRIPTION}&rdquo;.
        </li>
        <li>Set <Term>State</Term> to <Term>Enabled</Term>.</li>
        <li>
          Under <Term>User Permission Sets</Term>, add <Term>{SUGGESTED_PERMISSION_SET}</Term>, which only reads.
          Leave <Term>Company</Term> blank to cover every company, or add one line per company to report.
        </li>
        <li>Skip <Term>Grant Consent</Term> on that page: access is granted in Microsoft instead.</li>
      </Typography>
    </Stack>
  );
};
